import { Form } from 'components/organisms';
import { Button } from 'components/atoms';
import { MagellanPopups } from 'components/molecules';
import { userFormInitialValues } from 'components/organisms/forms/user/UserForm';
import { useParamId } from 'hooks/useParamId';
import i18next from 'i18next';
import middleware from 'middleware';
import { useGetUser } from 'services/userManagement';
import { PiPencilSimpleLine } from 'react-icons/pi';
import { useNavigate } from 'react-router-dom';
import { onErrorShowToast } from 'utils/service/errorHandler';
import { useDeleteUser } from 'services/userManagement/index';
import { PopupActions } from 'reactjs-popup/dist/types';
import { DefaultError } from '@tanstack/react-query';
import { useTranslation } from 'react-i18next';
import { usePutForcePasswordChange } from 'services/auth/forcePasswordChange';
import { BUTTON_VARIANT } from 'types/components/button/button.interface';
import { useGetPermissions } from 'services/userManagement/managePermissions';
import { useMemo } from 'react';

const User = () => {
  const userId = useParamId('admin/users');
  const { data: user, isLoading: isUserLoading } = useGetUser(userId);
  const { data: permissionsData, isLoading: isPermissionsLoading } =
    useGetPermissions(userId);
  const navigate = useNavigate();
  const i18nextPrefix = 'user';
  const { t } = useTranslation();
  const isLoading = isUserLoading || isPermissionsLoading;

  const { mutateAsync: deleteUser } = useDeleteUser();
  const { mutateAsync: forcePasswordChange } = usePutForcePasswordChange(userId);

  const combinedInitialValues = useMemo(() => {
    if (!user || !permissionsData) return userFormInitialValues;

    return {
      ...middleware.user.queryToForm(user,permissionsData),
    };
  }, [user, permissionsData]);

  const onRemoveUser = async (actions: PopupActions) => {
    if (user?.email) {
      await deleteUser(user.email, {
        onError: (error: DefaultError) => {
          actions.close();
          onErrorShowToast(error);
        },
        onSuccess: () => {
          navigate('/users');
        },
      });
    }
  };

  const onForcePasswordChange = async () => {
    if (user?.id) {
      await forcePasswordChange(user?.id, {
        onError: (error: DefaultError) => {
          onErrorShowToast(error);
        },
      })
    }
  }

  return (
    <Form.User
      i18nextPrefix={i18nextPrefix}
      tittle={user?.userName ?? i18next.t('user.title.individual')}
      initialValues={combinedInitialValues}
      backPath='settings/users'
      onSubmit={() => { }}
      status={{displayMode: true, isLoading}}
      topbarContent={
        <div className='flex  justify-end gap-2'>
          <Button
            variant={BUTTON_VARIANT.warning}
            onClick={onForcePasswordChange}
          >
            <Button.Text
              text={t('button.force-a-password-reset')}
            />
          </Button>
          <Button
            isClear
            onlyIcon
            onClick={() => {
              if (user) {
                navigate(`/settings/users/edit/${userId}/permissions`, { replace: true });
              }
            }}
          >
            <Button.Description description={t('button.edit')} />


            <Button.Icon icon={<PiPencilSimpleLine />} />
          </Button>
          <MagellanPopups.DeleteWarning
            name={user?.userName}
            onRemove={onRemoveUser}
          />
        </div>
      }
    />
  );
};

export default User;
